import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useCredits } from '../contexts/CreditsContext';
import { useLocation, useNavigate } from 'react-router-dom';
import CreditBalanceDisplay from '../components/credits/CreditBalanceDisplay';
import creditsService from '../services/creditsService';
import './CreditsPage.css';

const CreditsPage = () => {

  const location = useLocation();
  const navigate = useNavigate();
  const {
    transactions,
    refreshBalance
  } = useCredits();

  const [statistics, setStatistics] = useState({
    total_purchased: 0,
    total_spent: 0,
    total_transactions: 0
  });
  const [loadingStats, setLoadingStats] = useState(false);

  // Use ref to prevent unnecessary re-calculations
  const lastTransactionsLength = useRef(0);
  const isInitialized = useRef(false);

  // Credit packages available for purchase
  const creditPackages = [
    {
      amount: 50,
      price: '£5',
      popular: false,
      // payment_link: 'https://buy.stripe.com/test_28E4gA1CodGa7AK9gF38400',
      payment_link:  'https://buy.stripe.com/28E4gA1CodGa7AK9gF38400',
      description: 'Perfect for trying premium content',
      // icon: '🥉',
      icon: '🥈'
    },
    {
      amount: 100,
      price: '£10',
      popular: true,
      // payment_link: 'https://buy.stripe.com/test_dRmeVe6WIcC69IS64t38401',
      payment_link:  'https://buy.stripe.com/dRmeVe6WIcC69IS64t38401',
      description: 'Most popular choice for regular users',
      icon: '🥇'
    },
    // {
    //   amount: 250,
    //   price: '£25',
    //   popular: false,
    //   description: 'Best value for content enthusiasts',
    //   icon: '🥇',
    //   savings: 'Save 33%'
    // }
  ];

  // Stable function - doesn't change between renders
  const calculateStatsFromTransactionArray = useCallback((transactionArray) => {
    console.log('Calculating stats from transactions:', transactionArray);

    if (!transactionArray || transactionArray.length === 0) {
      setStatistics({
        total_purchased: 0,
        total_spent: 0,
        total_transactions: 0
      });
      return;
    }

    let totalPurchased = 0;
    let totalSpent = 0;

    transactionArray.forEach((transaction, index) => {
      const amount = Math.abs(transaction.amount || transaction.credits || 0);
      const type = (transaction.type || transaction.transaction_type || '').toLowerCase();
      const description = (transaction.description || transaction.message || '').toLowerCase();

      console.log(`Transaction ${index + 1}:`, {
        amount,
        type,
        description,
        originalAmount: transaction.amount || transaction.credits,
        transaction
      });

      const isPurchase = (
          (transaction.amount > 0 || transaction.credits > 0) ||
          type.includes('add') ||
          type.includes('purchase') ||
          type.includes('credit') ||
          type.includes('buy') ||
          description.includes('add') ||
          description.includes('purchase') ||
          description.includes('credit') ||
          description.includes('buy')
      );

      const isSpending = (
          (transaction.amount < 0 || transaction.credits < 0) ||
          type.includes('spent') ||
          type.includes('spend') ||
          type.includes('debit') ||
          type.includes('used') ||
          description.includes('spent') ||
          description.includes('spend') ||
          description.includes('debit') ||
          description.includes('used')
      );

      if (isPurchase && !isSpending) {
        totalPurchased += amount;
        console.log(`  → Counted as PURCHASE: +${amount}`);
      } else if (isSpending) {
        totalSpent += amount;
        console.log(`  → Counted as SPENDING: +${amount}`);
      } else {
        if ((transaction.amount || transaction.credits || 0) > 0) {
          totalPurchased += amount;
          console.log(`  → Default to PURCHASE (positive): +${amount}`);
        } else if ((transaction.amount || transaction.credits || 0) < 0) {
          totalSpent += amount;
          console.log(`  → Default to SPENDING (negative): +${amount}`);
        } else {
          console.log(`  → UNKNOWN transaction type, skipping`);
        }
      }
    });

    console.log('Final calculated stats:', {
      totalPurchased,
      totalSpent,
      totalTransactions: transactionArray.length
    });

    setStatistics({
      total_purchased: totalPurchased,
      total_spent: totalSpent,
      total_transactions: transactionArray.length
    });
  }, []); // Empty dependency array - this function is stable

  // Load statistics from API or calculate from balance
  const loadStatistics = useCallback(async () => {
    if (loadingStats) return; // Prevent concurrent calls

    try {
      setLoadingStats(true);
      console.log('Loading statistics...');

      // Try to get statistics from API
      try {
        const stats = await creditsService.getCreditStatistics();
        console.log('API Statistics:', stats);
        setStatistics({
          total_purchased: stats.total_purchased || stats.total_earned || 0,
          total_spent: stats.total_spent || 0,
          total_transactions: stats.total_transactions || stats.transactions_count || 0
        });
      } catch (apiError) {
        console.log('API statistics failed, calculating from balance data');
        // If API fails, get fresh balance data
        const balanceData = await creditsService.getCreditBalance();
        console.log('Balance data for stats:', balanceData);

        if (balanceData.success) {
          const freshTransactions = balanceData.recent_transactions || [];
          calculateStatsFromTransactionArray(freshTransactions);
        }
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
      // Fallback to current transactions if available
      if (transactions && transactions.length > 0) {
        calculateStatsFromTransactionArray(transactions);
      }
    } finally {
      setLoadingStats(false);
    }
  }, [loadingStats,transactions, calculateStatsFromTransactionArray]); // Only depend on loading state and stable calc function

  // Initial load effect - runs only once
  useEffect(() => {
    if (!isInitialized.current) {
      console.log('Initial load - refreshing balance and loading statistics');
      isInitialized.current = true;
      refreshBalance();
      loadStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - runs only on mount

  // Effect to recalculate stats when transactions change
  useEffect(() => {
    // Only recalculate if we actually have transactions and they've changed
    if (transactions &&
        transactions.length > 0 &&
        transactions.length !== lastTransactionsLength.current) {

      console.log('Transactions changed, recalculating stats');
      lastTransactionsLength.current = transactions.length;
      calculateStatsFromTransactionArray(transactions);
    }
  }, [transactions, calculateStatsFromTransactionArray]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isSuccess = params.get('success') === '1';
    const from = params.get('from');

    if (isSuccess && from) {
      // Optionally, you can show a toast or message before redirecting
      navigate(from, { replace: true });
    }
  }, [location, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';

    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getTransactionIcon = (type) => {
    switch (type?.toLowerCase()) {
      case 'purchase':
      case 'add':
      case 'credit':
        return '💳';
      case 'spent':
      case 'debit':
        return '💸';
      case 'refund':
        return '💰';
      default:
        return '📝';
    }
  };

  return (
      <div className="credits-page">
        <div className="credits-container">
          <header className="credits-header">
            <div className="header-content">
              <h1>💰 Manage Your Credits</h1>
              <p className="header-subtitle">
                Purchase credits to access premium content from your favorite creators
              </p>
              <div className="current-balance">
                <CreditBalanceDisplay showLabel={true} size="normal" />
              </div>
            </div>
          </header>

          <main className="credits-main">
            {/* Credit Packages Section */}
            <section className="credit-packages-section">
              <h2>Purchase Credits</h2>
              <p className="section-description">
                Choose a credit package that suits your needs. All purchases are instant!
              </p>

              <div className="credit-packages">
                {creditPackages.map((pkg) => (
                    <div
                        key={pkg.amount}
                        className={`credit-package ${pkg.popular ? 'popular' : ''}`}
                    >
                      {pkg.popular && <div className="popular-badge">Most Popular</div>}

                      <div className="package-header">
                        <div className="package-icon">{pkg.icon}</div>
                        <div className="package-amount">{pkg.amount} Credits</div>
                        {pkg.savings && <div className="package-savings">{pkg.savings}</div>}
                      </div>

                      <div className="package-price">{pkg.price}</div>
                      <div className="package-description">{pkg.description}</div>
                      <button
                        className={`purchase-button ${pkg.popular ? 'popular' : ''}`}
                        onClick={() => window.location.href = pkg.payment_link }
                      >
                        Buy Credits
                      </button>
                      {/* <button
                          className={`purchase-button ${pkg.popular ? 'popular' : ''}`}
                          onClick={() => handleAddCredits(pkg.amount)}
                          disabled={purchaseLoading !== null}
                      >
                        {purchaseLoading === pkg.amount ? (
                            <span className="loading-spinner">⏳ Processing...</span>
                        ) : (
                            `Add ${pkg.amount} Credits`
                        )}
                      </button> */}
                    </div>
                ))}
              </div>
            </section>

            {/* Statistics Section */}
            <section className="statistics-section">
              <h2>Your Credit Statistics</h2>
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-icon">📊</div>
                  <div className="stat-content">
                    <div className="stat-value">
                      {loadingStats ? '...' : statistics.total_purchased}
                    </div>
                    <div className="stat-label">Total Purchased</div>
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">💸</div>
                  <div className="stat-content">
                    <div className="stat-value">
                      {loadingStats ? '...' : statistics.total_spent}
                    </div>
                    <div className="stat-label">Total Spent</div>
                  </div>
                </div>
                <div className="stat-item">
                  <div className="stat-icon">🎯</div>
                  <div className="stat-content">
                    <div className="stat-value">
                      {loadingStats ? '...' : statistics.total_transactions}
                    </div>
                    <div className="stat-label">Transactions</div>
                  </div>
                </div>
              </div>
            </section>

            {/* Transaction History Section */}
            <section className="transaction-history-section">
              <h2>Transaction History</h2>

              {!transactions || transactions.length === 0 ? (
                  <div className="no-transactions">
                    <div className="no-transactions-icon">📝</div>
                    <p>No transactions yet</p>
                    <p className="no-transactions-subtitle">
                      Your credit purchases and spending will appear here
                    </p>
                  </div>
              ) : (
                  <div className="transactions-list">
                    {transactions.slice(0, 10).map((transaction, index) => (
                        <div key={transaction.id || index} className="transaction-item">
                          <div className="transaction-icon">
                            {getTransactionIcon(transaction.type || transaction.transaction_type)}
                          </div>
                          <div className="transaction-details">
                            <div className="transaction-description">
                              {transaction.description ||
                                  transaction.message ||
                                  `${transaction.type || transaction.transaction_type || 'Credit'} transaction`}
                            </div>
                            <div className="transaction-date">
                              {formatDate(transaction.created_at || transaction.date || transaction.timestamp)}
                            </div>
                          </div>
                          <div className={`transaction-amount ${(transaction.amount || transaction.credits) > 0 ? 'positive' : 'negative'}`}>
                            {(transaction.amount || transaction.credits) > 0 ? '+' : ''}{transaction.amount || transaction.credits}
                          </div>
                        </div>
                    ))}

                    {transactions.length > 10 && (
                        <div className="show-more">
                          <button className="show-more-button" onClick={refreshBalance}>
                            Refresh Transactions
                          </button>
                        </div>
                    )}
                  </div>
              )}
            </section>
          </main>

          <footer className="credits-footer">
            <div className="footer-info">
              <h3>💡 How Credits Work</h3>
              <ul>
                <li>Credits are used to access premium content from creators</li>
                <li>Each piece of content has a credit cost set by the creator</li>
                <li>Credits never expire and are tied to your account</li>
                <li>All purchases are instant and secure</li>
              </ul>
            </div>
          </footer>
        </div>
      </div>
  );
};

export default CreditsPage;